import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import useFileUploadStore from "../../../../stores/fileUploadStore";
import {
  bulkUploadsQueryKeys,
  useApproveBulkBatch,
  useGetBatchDetail,
  useGetBatchRecipientsSummation,
} from "../../../../hooks/queries/useBulkUploadsQueries";
import { extractAxiosErrorMessage } from "../../../../services/utils/errorUtils";
import { toastService } from "../../../../providers";

export function useStep3() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  const { disbursementId, batchData, uiState } = useFileUploadStore(
    useShallow((s) => ({
      disbursementId: s.disbursementId,
      batchData: s.batchData,
      uiState: s.uiState,
    })),
  );

  const { mutate: approveBatch, isPending: isApproving } =
    useApproveBulkBatch();

  const { refetch: refetchBatchDetail } = useGetBatchDetail(disbursementId, {
    enabled: false,
    staleTime: 0,
  });

  const { data: recipientsData } =
    useGetBatchRecipientsSummation(disbursementId);
  const totalAmount = recipientsData?.recipientsSummation?.amount ?? null;

  const handleApprove = () => {
    useFileUploadStore.getState().setApproving();

    approveBatch(disbursementId, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: bulkUploadsQueryKeys.batchDetail(disbursementId),
        });
        setSuccessModalOpen(true);
      },
      onError: (error) => {
        const store = useFileUploadStore.getState();
        store.setReadyForApproval(store.batchData);
        toastService.error(
          extractAxiosErrorMessage(error, "Approval failed. Please try again."),
        );
      },
    });
  };

  const handlePostEditRefetch = async () => {
    queryClient.invalidateQueries({
      queryKey: [
        ...bulkUploadsQueryKeys.all,
        "batchValidationRecipients",
        disbursementId,
      ],
    });
    queryClient.invalidateQueries({
      queryKey: bulkUploadsQueryKeys.batchRecipientsSummation(disbursementId),
    });

    const { data } = await refetchBatchDetail();
    if (!data) return;

    const store = useFileUploadStore.getState();
    if (data.status === "PENDING_APPROVAL") {
      const { paymentChannel, recipientCount, batchId } = data;
      store.setReadyForApproval({ paymentChannel, recipientCount, batchId });
    }
  };

  const handleNewUpload = () => {
    setSuccessModalOpen(false);
    useFileUploadStore.getState().reset();
  };

  const handleViewHistory = () => {
    setSuccessModalOpen(false);
    useFileUploadStore.getState().reset();
    navigate("/bulkpayments");
  };

  return {
    disbursementId,
    batchData,
    totalAmount,
    uiState,
    isApproving,
    successModalOpen,
    handleApprove,
    handlePostEditRefetch,
    handleNewUpload,
    handleViewHistory,
  };
}
